export const errorHandler = {
    throwNotFound() {
        return "Failed to Connect to Server (URL Not Found)!";
    },

    throwTimeOut() {
        return "Connection Timed Out!";
    },

    escapeHtml(str) {
        if (!str) return str;
        return str
            .replace(/</g, "&lt;")
            .replace(/>/g, "&gt;");
    },

    /**
     * Detect the indexed-character error shape the backend sometimes returns
     * (e.g. {"0":"I","1":"t","2":"e","3":"m","4":" ",...,"46":"d"} for
     * "Item Code : ... not found") and reconstruct the original message.
     *
     * This happens when the upstream server returns a string but the serializer
     * (PHP json_encode(str_split($msg)) or similar) produces a flat object with
     * numeric-string keys. We only treat it as such when every key is a
     * non-negative integer string and the keys form a contiguous 0..N sequence.
     *
     * @param {object} response - Error response body to test
     * @returns {string|null} Reconstructed message, or null if pattern doesn't match
     */
    reconstructIndexedCharacterMessage(response) {
        if (!response || typeof response !== 'object' || Array.isArray(response)) {
            return null;
        }
        const keys = Object.keys(response);
        // Need at least 2 keys to safely distinguish from a regular single-field
        // object that happens to use the key "0" (very unlikely, but cheap to skip).
        if (keys.length < 2) return null;

        const numericKeys = [];
        for (const key of keys) {
            if (!/^(?:0|[1-9][0-9]*)$/.test(key)) return null;
            numericKeys.push(Number(key));
        }

        const max = Math.max(...numericKeys);
        if (numericKeys.length !== max + 1) return null; // must be contiguous 0..N
        if (!numericKeys.every((n, i) => n === i)) return null;

        let joined = '';
        for (let i = 0; i <= max; i++) {
            const piece = response[String(i)];
            if (piece === undefined || piece === null) return null;
            joined += String(piece);
        }
        return joined;
    },

    /**
     * Plain server/error text for storage and UI (no HTML escape).
     * Peels nested outbox wrappers, JSON-string fields, and char-index maps
     * so callers can show what the server said instead of packaging noise.
     *
     * @param {*} response
     * @param {number} [depth]
     * @returns {string}
     */
    toPlainErrorText(response, depth = 0) {
        if (response == null || response === '') return '';
        if (depth > 8) return String(response);
        if (typeof response === 'string') {
            const trimmed = response.trim();
            if (
                (trimmed.startsWith('{') && trimmed.endsWith('}')) ||
                (trimmed.startsWith('[') && trimmed.endsWith(']'))
            ) {
                try {
                    return this.toPlainErrorText(JSON.parse(trimmed), depth + 1) || trimmed;
                } catch (_) {
                    return response;
                }
            }
            return response;
        }
        if (typeof response !== 'object') return String(response);

        if (Array.isArray(response)) {
            if (response.length === 0) return '';
            return this.toPlainErrorText(response[0], depth + 1);
        }

        const indexed = this.reconstructIndexedCharacterMessage(response);
        if (indexed && indexed.trim()) return indexed.trim();

        const preferredKeys = [
            'errorMessage',
            'message',
            'Message',
            'lastError',
            'response_message',
            'error',
            'Error',
        ];
        for (const key of preferredKeys) {
            if (response[key] == null || response[key] === '') continue;
            // Skip meta wrappers like reason: "Non-retriable error" when a nested body exists
            if (key === 'error' || key === 'Error') {
                const nested = this.toPlainErrorText(response[key], depth + 1);
                if (nested && nested.trim()) return nested.trim();
                continue;
            }
            const nested = this.toPlainErrorText(response[key], depth + 1);
            if (nested && nested.trim()) return nested.trim();
        }

        if (response.response != null) {
            const nested = this.toPlainErrorText(response.response, depth + 1);
            if (nested && nested.trim()) return nested.trim();
        }
        if (response.serverResponse != null) {
            const nested = this.toPlainErrorText(response.serverResponse, depth + 1);
            if (nested && nested.trim()) return nested.trim();
        }
        if (response.Print != null && String(response.Print).toLowerCase().includes('failed')) {
            return String(response.Print);
        }
        if (response.reason && typeof response.reason === 'string') {
            return response.reason;
        }
        return '';
    },

    extractErrorMessage(response) {
        if (response == null) return response;
        if (typeof response !== 'object') {
            const plain = this.toPlainErrorText(response);
            return this.escapeHtml(plain || String(response));
        }

        const plain = this.toPlainErrorText(response);
        if (plain && plain.trim()) {
            return this.escapeHtml(plain.trim());
        }

        // Last resort: avoid dumping huge objects when possible
        try {
            return this.escapeHtml(JSON.stringify(response));
        } catch (_) {
            return this.escapeHtml(String(response));
        }
    },

    franchiseLoginError(err) {
        let errorMessage = err;

        if (err.response) {
            console.error(err.response);
            errorMessage = err.response.status === 404 ? this.throwNotFound() : this.extractErrorMessage(err.response.data);
        } else if (err.code === "ECONNABORTED") {
            errorMessage = this.throwTimeOut();
        } else {
            console.error(err);
            errorMessage = err.message;
        }

        if (errorMessage === "Network Error") {
            errorMessage = "Please check your internet connection.";
        }

        return errorMessage;
    },

    /**
     * Map known table/sale business failures to waiter-safe copy.
     * Founder + Victoria brief 2026-08-09 (Joseph WaiterStation): never show
     * raw "Table Does not Exist" or EMPTY_SAVE_* codes to operators.
     * docs: USER-FACING-ERRORS; tableSyncGuards TABLE_NOT_READY_MSG
     *
     * @param {unknown} rawMessage
     * @param {{ code?: string, status?: number }} [meta]
     * @returns {string|null} friendly message or null if not a table/sale case
     */
    mapTableOperatorMessage(rawMessage, meta = {}) {
        const text = String(rawMessage || '');
        const code = String(meta.code || '').trim();
        const status = Number(meta.status || 0);

        // Prefer structured code when present (axios interceptor / 409 body).
        if (code === 'EMPTY_SAVE_REJECTED' || /EMPTY_SAVE_REJECTED/i.test(text)) {
            return 'This table still has items. Refresh to load them.';
        }
        if (code === 'EMPTY_SAVE_BLOCKED' || /EMPTY_SAVE_BLOCKED|Cannot save empty table/i.test(text)) {
            return 'This table still has items. Refresh to load them.';
        }
        if (code === 'TABLE_STATE_CONFLICT' || /TABLE_STATE_CONFLICT/i.test(text)) {
            return 'Table was modified by another user. Re-fetching...';
        }
        if (
            code === 'TABLE_MISSING' ||
            /table\s+does\s+not\s+exist|table\s+not\s+found|table\s+not\s+available/i.test(text)
        ) {
            return 'This table is no longer available. Refresh and try again.';
        }
        // 4xx table/sale paths with empty body still get a safe generic
        if ((status === 404 || status === 406 || status === 409) && /table|sale|savetable/i.test(text)) {
            return 'This table is no longer available. Refresh and try again.';
        }
        if (!text) return null;
        return null;
    },

    /**
     * Normalize an axios-style error into operator-safe table wording when applicable.
     * Used by the global axios response interceptor and tomcatError.
     * Does not toast — only mutates/returns a friendly Error for callers.
     *
     * @param {unknown} err
     * @returns {Error|unknown}
     */
    normalizeTableAxiosError(err) {
        if (!err || !err.response) return err;
        const status = Number(err.response.status || 0);
        if (status < 400 || status >= 500) {
            // Still map 5xx bodies that embed table-missing text
            if (status >= 500) {
                const bodyMsg = this.extractErrorMessage(err.response.data);
                const mapped5 = this.mapTableOperatorMessage(bodyMsg, {
                    code: err.response?.data?.code,
                    status,
                });
                if (mapped5) {
                    const e = new Error(mapped5);
                    e.code = err.response?.data?.code || err.code || 'TABLE_ERROR';
                    e.response = err.response;
                    e.isAxiosError = true;
                    e.config = err.config;
                    e.operatorSafe = true;
                    return e;
                }
            }
            return err;
        }
        const body = err.response.data;
        const code = String(
            (body && typeof body === 'object' && (body.code || body.errorCode || body.error)) ||
            err.code ||
            ''
        ).trim();
        const bodyMsg = this.extractErrorMessage(body);
        const mapped = this.mapTableOperatorMessage(bodyMsg || err.message, { code, status });
        if (!mapped) return err;
        const e = new Error(mapped);
        e.code = code || err.code || 'TABLE_ERROR';
        e.response = err.response;
        e.isAxiosError = true;
        e.config = err.config;
        e.operatorSafe = true;
        // Preserve original for logging / auto-recovery (EMPTY_SAVE_REJECTED retry)
        e.originalMessage = bodyMsg || err.message;
        e.originalCode = code;
        return e;
    },

    /**
     * Install once on the shared axios instance so every savetable/tables call
     * surfaces waiter-safe copy — never raw "Table Does not Exist".
     * Idempotent. Auto-recovery for EMPTY_SAVE_REJECTED stays in AddOrUpdateTable
     * (postTableSaveWithConflictRetry); interceptor only rewrites the message.
     *
     * @param {import('axios').AxiosStatic|import('axios').AxiosInstance} axiosLib
     */
    installTableErrorInterceptor(axiosLib) {
        if (!axiosLib || !axiosLib.interceptors || !axiosLib.interceptors.response) return;
        if (axiosLib.__lytningTableErrorInterceptorInstalled) return;
        axiosLib.__lytningTableErrorInterceptorInstalled = true;
        const self = this;
        axiosLib.interceptors.response.use(
            (response) => response,
            (error) => {
                try {
                    const normalized = self.normalizeTableAxiosError(error);
                    return Promise.reject(normalized);
                } catch (_) {
                    return Promise.reject(error);
                }
            }
        );
    },

    tomcatError(err, isSweetalert = false, title = "An Error Occurred") {
        // Intentional empty-save / silent hydrate — no toast, no Swal
        if (err && (err.silent === true || err.code === 'EMPTY_SAVE_BLOCKED' && err.silent !== false && err.suppressToast === true)) {
            const quiet = new Error(
                err.message || 'This table still has items. Refresh to load them.'
            );
            quiet.code = err.code || 'EMPTY_SAVE_BLOCKED';
            quiet.silent = true;
            throw quiet;
        }

        let errorMessage = err;

        if (err.response) {
            console.error(err.response);
            const status = err.response.status;
            const bodyMsg = this.extractErrorMessage(err.response.data);
            const code = String(
                (err.response.data && typeof err.response.data === 'object' &&
                    (err.response.data.code || err.response.data.errorCode)) ||
                err.code ||
                ''
            ).trim();
            const mappedEarly = this.mapTableOperatorMessage(bodyMsg || err.message, {
                code,
                status,
            });

            if (status === 404) {
                // Prefer table-missing wording over generic URL-not-found when body says so
                errorMessage = mappedEarly || this.throwNotFound();
            } else if (status === 500) {
                if (mappedEarly) {
                    errorMessage = mappedEarly;
                } else {
                    return Swal.fire({
                        title,
                        icon: "error",
                        html: `<p>Please contact support: +260958168829</p><span>${bodyMsg}</span>`,
                        showCloseButton: true,
                    });
                }
            } else if (mappedEarly) {
                // 406 / 409 / other 4xx table business failures
                errorMessage = mappedEarly;
            } else {
                errorMessage = bodyMsg;
            }
        } else if (err.code === "ECONNABORTED") {
            errorMessage = this.throwTimeOut();
        } else {
            console.error(err);
            errorMessage = err.message;
        }

        if (errorMessage === "Network Error") {
            errorMessage = "Please check your internet connection.";
        }

        // Second pass for non-response Error objects that already carry raw BE text
        const mapped = this.mapTableOperatorMessage(errorMessage, {
            code: err?.code,
            status: err?.response?.status,
        });
        if (mapped) {
            errorMessage = mapped;
        }

        if (!isSweetalert) {
            responses.throwErr(errorMessage, title);
        }

        throw new Error(errorMessage);
    },

    customerKazangError(err, isSweetalert = false) {
        console.log(err);
        let errorMessage = err;

        if (err.response) {
            console.error(err.response);
            errorMessage = err.response.status === 404 ? this.throwNotFound() : this.extractErrorMessage(err.response.data);
        } else if (err.Error) {
            errorMessage = err.Message;
        } else if (err.code === "ECONNABORTED") {
            errorMessage = this.throwTimeOut();
        }

        if (!isSweetalert) {
            responses.throwErr(errorMessage);
        }

        throw new Error(errorMessage);
    },
};